// Boiler Plate Imports
import styled from "styled-components";
import colors from "../../theme/colors";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";
// Conatiner Component Imports
import { Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";
import { ItemLink, TableComponent } from "../../components/Table/dataGridPro";

const HalfWidthWidgetContainer = styled.div`
  margin: 20px 0;
  display: flex;
  gap: 20px;
  @media (max-width: 900px) {
    flex-direction: column-reverse;
  }
`;
const HalfWidthWidget = styled(Card)`
  width: 100%;
  padding: 20px;
`;
const WidgetHeader = styled.div`
  font-size: 17px;
  font-weight: bold;
  border-bottom: 1px solid ${colors.gray};
  padding: 0 0 6px 0;
  margin: 0 0 6px 0;
`;

const WidgetSubHeader = styled.div`
  font-size: 12px;
  color: ${colors.gray};
`;

const WidgetHeaderContainer = styled.div`
  width: 100%;
`;

const WidgetBody = styled.div`
  font-size: 12px;
  color: ${colors.gray};
`;

const BodyHeader = styled.div`
  margin: 20px 0 8px;
  font-size: 15px;
  color: ${colors.black};
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusTag = styled.div`
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 87px;
  font-weight: bold;
  background: ${colors.grayLight};
  color: ${colors.grayDark};
  border: 1px solid ${colors.gray};
  &.alarm {
    background: ${colors.redLightError};
    color: ${colors.redError};
    border: 1px solid ${colors.redError};
  }
  &.progress {
    background: ${colors.yellowLight};
    color: ${colors.yellow};
    border: 1px solid ${colors.yellow};
  }
  &.resolved {
    background: ${colors.greenLightSuccess};
    color: ${colors.greenSuccess};
    border: 1px solid ${colors.greenSuccess};
  }
`;

const Table = React.memo(TableComponent);

const OrganizationAlarmsContainer = () => {
  const organization = useSelector((state) => state.organization);

  const [alarmTableRows, setAlarmTableRows] = useState([]);
  const [loadingAlarms, setLoadingAlarms] = useState(true);
  const [reloadData, setReloadData] = useState(false);

  const dispatch = useDispatch();

  const { organizationId } = useParams();

  const tableColumns = useMemo(() => {
    return [
      {
        headerName: "Alarm Type",
        field: "alarmInfoDisplayName",
        valueGetter: (value, row) => {
          return row.alarmInfoDisplayName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${organizationId}/alarms/${row.id}`}>
              {row.alarmInfoDisplayName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Alarm Status",
        field: "active",
        display: "flex",
        valueGetter: (value, row) => {
          return row.active === "true" ? "Active" : "Resolved";
        },
        renderCell: ({ row }) => {
          const statusString = row.active === "true" ? "Active" : "Resolved";
          return (
            <StatusTag className={row.active === "true" ? "alarm" : "resolved"}>
              {statusString}
            </StatusTag>
          );
        },
      },
      {
        headerName: "Resolution Status",
        field: "resolutionStatus",
        display: "flex",
        valueGetter: (value, row) => {
          let statusString = "Not Started";
          if (row.resolutionStatus === "resolved") {
            statusString = "Resolved";
          } else if (row.resolutionStatus === "progress") {
            statusString = "In Progress";
          }
          return statusString;
        },
        renderCell: ({ row }) => {
          let statusString = "Not Started";
          let className = "alarm";
          if (row.resolutionStatus === "resolved") {
            statusString = "Resolved";
            className = "resolved";
          } else if (row.resolutionStatus === "progress") {
            statusString = "In Progress";
            className = "progress";
          }
          return <StatusTag className={className}>{statusString}</StatusTag>;
        },
      },
      {
        headerName: "Tank Name",
        field: "tankName",
        valueGetter: (value, row) => {
          return row.tankName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${organizationId}/tanks/${row.tankId}`}>
              {row.tankName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Tank Location",
        field: "locationName",
        valueGetter: (value, row) => {
          return row.locationName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${organizationId}/locations/${row.locationId}`}>
              {row.locationName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Alarm Created",
        field: "created",
        type: "dateTime",
        valueGetter: (value, row) => {
          return new Date(row.created);
        },
      },
      {
        headerName: "Alarm Resolved",
        field: "resolved",
        type: "dateTime",
        valueGetter: (value, row) => {
          if (!row.resolvedDate) {
            return null;
          }
          return new Date(row.resolvedDate);
        },
        renderCell: ({ row }) => {
          if (row.resolvedDate) {
            return new Date(row.resolvedDate).toLocaleString();
          } else {
            return "-";
          }
        },
      },
    ];
  }, []);

  useEffect(() => {
    if (organizationId) {
      dataLayer.GetOrganizationAlarms();
    }
  }, [organizationId]);

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name,
          link: "/organization",
        },
        {
          title: "Alarms",
          link: "/alarms",
        },
      ]),
    );
  }, [organization]);

  const dataLayer = {
    GetOrganizationAlarms: async () => {
      setLoadingAlarms(true);
      const alarms = await API.Alarm.AlarmsGet({ organizationId });
      const alarmsRows = alarms.map((alarm) => {
        return {
          ...alarm,
          alarmInfoDisplayName: alarm.alarmInfo.displayName,
          tankName: alarm.tank.name, //flatten for the table and sorting
          locationName: alarm.tank.location.name,
        };
      });
      alarmsRows.sort((a, b) => {
        return b.created - a.created;
      });
      setAlarmTableRows(alarmsRows);
      setLoadingAlarms(false);
      return alarmsRows;
    },
  };

  return (
    <>
      
      <Card>
        <TableHeader>
          <div>
            <h2>Alarms</h2>
            <TableSubHeader>
              Below is a list of all Alarms within this Organization
            </TableSubHeader>
          </div>
        </TableHeader>
        <Table
          columns={tableColumns}
          autoResizeColumns={true}
          label={""}
          isAdminView={false}
          reloadData={reloadData}
          placeholder="There are no alarms yet"
          getRowsFunction={dataLayer.GetOrganizationAlarms}
          parentLoading={true}
          initialState={{
            // pinnedColumns: { left: ["name"] },
            sorting: {
              sortModel: [{ field: "created", sort: "desc" }],
            },
          }}
        />
      </Card>
      <HalfWidthWidgetContainer>
        <HalfWidthWidget>
          <WidgetHeaderContainer>
            <WidgetHeader>Types of Alarms</WidgetHeader>
            <WidgetSubHeader>
              The following types of alarms are supported by the Boreas system
            </WidgetSubHeader>
          </WidgetHeaderContainer>
          <WidgetBody>
            <BodyHeader>Low Weight</BodyHeader>
            <div>
              When a tank's liquid nitrogen level falls below the tank's set
              threshold
            </div>
            <BodyHeader>High Consumption Rate</BodyHeader>
            <div>
              When a tank's liquid nitrogen consumption rate is above tank's
              normal levels
            </div>
            <BodyHeader>Low External Temperature</BodyHeader>
            <div>
              When a tank's external temperature is lower than the tank's set
              threshold
            </div>
            <BodyHeader>Low Battery</BodyHeader>
            <div>When a tank's scale battery is low</div>
            <BodyHeader>Tank Offline</BodyHeader>
            <div>
              When a tank has been unreachable for over 3 hours 30 minutes
            </div>
          </WidgetBody>
        </HalfWidthWidget>
      </HalfWidthWidgetContainer>
    </>
  );
};

export default OrganizationAlarmsContainer;
